import React from 'react';
import { CustomEmptyState } from './CustomEmptyState';

interface WithCustomEmptyStateOptions {
  resource: string;
  title?: string;
  subtitle?: string;
  actionText?: string;
  onAction?: () => void;
  hideAction?: boolean;
}

/**
 * Higher-order function that creates a custom empty state component
 * for any React Admin List component
 */
export const withCustomEmptyState = (options: WithCustomEmptyStateOptions) => {
  const {
    resource,
    title,
    subtitle,
    actionText,
    onAction,
    hideAction = false,
  } = options;

  return () => (
    <CustomEmptyState
      resource={resource}
      title={title}
      subtitle={subtitle}
      actionText={actionText}
      onAction={hideAction ? undefined : onAction}
    />
  );
};

/**
 * Quick helper for creating empty states with default create actions
 */
export const createEmptyStateComponent = (
  resource: string,
  onCreateAction?: () => void,
  customOptions?: Partial<WithCustomEmptyStateOptions>
) => {
  return withCustomEmptyState({
    resource,
    onAction: onCreateAction,
    ...customOptions,
  });
};

/**
 * Default empty states for common resources
 */
export const EmptyStates = {
  // Main resources
  users: withCustomEmptyState({
    resource: 'users',
    onAction: () => window.location.href = '/#/users/create',
  }),
  
  equipment: withCustomEmptyState({
    resource: 'equipment',
    onAction: () => window.location.href = '/#/equipment/create',
  }),
  
  forms: withCustomEmptyState({
    resource: 'forms',
    onAction: () => (window.location.href = '/configuration/forms/create'),
  }),
  
  articles: withCustomEmptyState({
    resource: 'articles',
    onAction: () => window.location.href = '/#/articles/create',
  }),
  
  // Driver-related resources (these usually don't have standalone creation)
  'driver-licenses': withCustomEmptyState({
    resource: 'driver-licenses',
    title: 'No Driver Licenses',
    subtitle: 'Driver licenses will appear here when drivers complete their license information through their profiles.',
    hideAction: true,
  }),
  
  'driver-certifications': withCustomEmptyState({
    resource: 'driver-certifications',
    title: 'No Driver Certifications',
    subtitle: 'Driver certifications will appear here when drivers add their professional certifications through their profiles.',
    hideAction: true,
  }),
  
  'driver-education': withCustomEmptyState({
    resource: 'driver-education',
    title: 'No Driver Education Records',
    subtitle: 'Education and training records will appear here when drivers complete their education information.',
    hideAction: true,
  }),
  
  'driver-employment': withCustomEmptyState({
    resource: 'driver-employment',
    title: 'No Employment History',
    subtitle: 'Driver employment records will appear here when drivers add their work experience through their profiles.',
    hideAction: true,
  }),
  
  'driver-vehicles': withCustomEmptyState({
    resource: 'driver-vehicles',
    title: 'No Vehicle Experience',
    subtitle: 'Driver vehicle experience will appear here when drivers complete their vehicle operation history.',
    hideAction: true,
  }),
  
  'completed-forms': withCustomEmptyState({
    resource: 'completed-forms',
    title: 'No Completed Forms',
    subtitle: 'Form submissions and responses will appear here once users start completing forms.',
    hideAction: true,
  }),
};